import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { X, Save, RulerDimensionLine, Info, AlertTriangle } from 'lucide-react';

const SpecForm = ({ isOpen, onClose, onSuccess, editingSpec = null }) => {
  const { userRole } = useAuth();
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    prioridad: 1
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [maxPriority, setMaxPriority] = useState(1);
  const [submitError, setSubmitError] = useState('');

  // Fetch max priority when component mounts
  useEffect(() => {
    if (isOpen && !editingSpec) {
      fetchMaxPriority();
    }
  }, [isOpen, editingSpec]);

  const fetchMaxPriority = async () => {
    try {
      const q = query(collection(db, 'specs'), orderBy('prioridad', 'desc'));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const highestSpec = querySnapshot.docs[0].data();
        setMaxPriority((highestSpec.prioridad || 0) + 1);
      } else {
        setMaxPriority(1);
      }
    } catch (error) {
      // Handle case where collection doesn't exist yet
      console.log('Specs collection may not exist yet, starting with priority 1');
      setMaxPriority(1);
    }
  };

  // Populate form when editing
  useEffect(() => {
    if (editingSpec) {
      setFormData({
        nombre: editingSpec.nombre || '',
        descripcion: editingSpec.descripcion || '',
        prioridad: editingSpec.prioridad || 1
      });
    } else {
      // Reset form for new spec
      setFormData({
        nombre: '',
        descripcion: '',
        prioridad: maxPriority
      });
    }
    setErrors({});
  }, [editingSpec, isOpen, maxPriority]);

  const validateForm = async () => {
    const newErrors = {};

    // Validate spec name
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre de la especificación es requerido';
    } else {
      // Check for duplicate spec names
      try {
        const specsQuery = query(collection(db, 'specs'));
        const specsSnapshot = await getDocs(specsQuery);
        
        const duplicateFound = specsSnapshot.docs.some(doc => {
          const existingSpec = doc.data();
          const isDifferentSpec = !editingSpec || doc.id !== editingSpec.id;
          return isDifferentSpec && 
                 existingSpec.nombre?.toLowerCase() === formData.nombre.trim().toLowerCase();
        });

        if (duplicateFound) {
          newErrors.nombre = 'Ya existe una especificación con este nombre';
        }
      } catch (error) {
        // Handle case where collection doesn't exist yet - this is normal for first-time use
        console.log('Specs collection may not exist yet, skipping duplicate check');
        // Don't set an error since this is expected behavior when no specs exist yet
      }
    }

    // Validate priority
    if (!formData.prioridad || formData.prioridad < 1) {
      newErrors.prioridad = 'La prioridad debe ser un número mayor a 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    // Debug: Log current authentication state
    console.log('Current user:', userRole);
    console.log('Form data:', formData);

    // Check permissions
    if (!userRole || !['admin', 'shopkeeper'].includes(userRole)) {
      setSubmitError(`No tienes permisos para ${editingSpec ? 'editar' : 'crear'} especificaciones. Tu rol actual es: ${userRole || 'sin rol'}`);
      return;
    }

    setLoading(true);

    try {
      // Clean up data
      const cleanedData = {
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim() || null,
        prioridad: parseInt(formData.prioridad),
        updatedAt: new Date()
      };

      if (editingSpec) {
        // Update existing spec
        await updateDoc(doc(db, 'specs', editingSpec.id), cleanedData);
      } else {
        // Create new spec
        cleanedData.createdAt = new Date();
        await addDoc(collection(db, 'specs'), cleanedData);
      }
      
      // Invalidate specs cache to ensure fresh data everywhere
      try {
        const { invalidateCache } = await import('../../lib/cacheService');
        invalidateCache('specs');
      } catch (error) {
        console.error('Error invalidating specs cache:', error);
      }
      
      // Reset form and close
      resetForm();
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving specification:', error);
      setSubmitError('Error al guardar la especificación. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      prioridad: maxPriority
    });
    setErrors({});
    setSubmitError('');
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <RulerDimensionLine className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingSpec ? 'Editar Especificación' : 'Nueva Especificación'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {submitError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md mb-6">
              {submitError}
            </div>
          )}
          <div className="space-y-6">
            {/* Specification Name */}
            <div>
              <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nombre de la Especificación *
              </label>
              <input
                type="text"
                id="nombre"
                value={formData.nombre}
                onChange={(e) => handleInputChange('nombre', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-yellow-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.nombre ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Ej: Potencia (HP), Caudal (L/min), Voltaje (V)..."
                disabled={loading}
              />
              {errors.nombre && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.nombre}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Use un formato descriptivo que incluya las unidades de medida entre paréntesis.
              </p>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Descripción
              </label>
              <textarea
                id="descripcion"
                value={formData.descripcion}
                onChange={(e) => handleInputChange('descripcion', e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-yellow-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                placeholder="Descripción opcional de la especificación y su uso..."
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Descripción opcional para explicar el propósito o contexto de esta especificación.
              </p>
            </div>

            {/* Priority */}
            <div>
              <label htmlFor="prioridad" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Prioridad *
              </label>
              <input
                type="number"
                id="prioridad"
                min="1"
                max="999"
                value={formData.prioridad}
                onChange={(e) => handleInputChange('prioridad', parseInt(e.target.value) || 1)}
                className={`w-24 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-yellow-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.prioridad ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                disabled={loading}
              />
              {errors.prioridad && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.prioridad}</p>
              )}

              {!editingSpec && (
                <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                    <div className="text-sm text-yellow-800 dark:text-yellow-400">
                      <p className="font-medium">Prioridad Sugerida: #{maxPriority}</p>
                      <p>Esta especificación se agregará al final de la lista actual. 
                      Puede ajustar la prioridad ahora o reordenar después usando drag-and-drop.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-yellow-400 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 dark:bg-yellow-700 text-white rounded-md hover:bg-yellow-700 dark:hover:bg-yellow-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:focus:ring-yellow-400 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Guardando...' : (editingSpec ? 'Actualizar Especificación' : 'Guardar Especificación')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SpecForm;